"use client"

import type { ReactNode } from "react"
import clsx from "clsx"
import useMeasure from "react-use-measure"

import type { InferReturn, PropsWithClassName } from "~/@types/generics"
import type { serializeCollectionNavigationItems } from "~/data/collection-navigation-items/serializer"
import useBreakpoint from "~/hooks/useBreakpoint"
import { useScrollDirection } from "~/hooks/useScrollDirection"
import { CollectionNavigation } from "~/components/ui/Collection/CollectionDefault/components/CollectionNavigation"
import { useUSPBannerHeight } from "~/managers/USPBannerManager"

import { sprinkles, type Sprinkles } from "~/styles/sprinkles.css"
import { px } from "~/styles/utils/px"
import { hideFrom } from "~/styles/utils/show-hide"
import { elements } from "~/styles/variables/elements"

import * as css from "./styles.css"

type HeaderControlsContainerProps = PropsWithClassName<{
  navigation: InferReturn<typeof serializeCollectionNavigationItems>
  children?: ReactNode
  navigationSprinklesCss?: Sprinkles
}>

function HeaderControlsContainer({
  className,
  navigation,
  children,
  navigationSprinklesCss,
}: HeaderControlsContainerProps) {
  const { isScrollUp } = useScrollDirection()
  const [measureRef, measure] = useMeasure()
  const uspBannerHeight = useUSPBannerHeight()[0]
  const isMobile = useBreakpoint("lg")

  const baseHeaderStyle = `${elements.navigation.height} + ${
    isMobile ? elements.navigation.mobileTop : elements.navigation.desktopTop
  } + ${px(uspBannerHeight)}`

  const headerControlsStyle = {
    top: isScrollUp ? `calc(${baseHeaderStyle})` : `calc(${baseHeaderStyle} - ${measure.height}px)`,
  }

  return (
    <div
      data-comp="UI/Collection/HeaderControlsContainer"
      className={clsx(css.HeaderControlsContainer, className)}
      style={headerControlsStyle}
    >
      {navigation && navigation?.length > 0 && (
        <div
          className={clsx(
            css.HeaderControlsMobileFilters,
            hideFrom("tablet", "block"),
            sprinkles({ pT: 20, ...(navigationSprinklesCss ?? {}) })
          )}
          ref={measureRef}
        >
          <CollectionNavigation
            navigationMobileClassName={css.HeaderControlsMobileNavigation}
            key={`collection_navigation_mobile`}
            navigation={navigation}
          />
        </div>
      )}

      {children && <div className={css.HeaderControlsBlock}>{children}</div>}
    </div>
  )
}

export { HeaderControlsContainer, type HeaderControlsContainerProps }
